import React, { useState } from 'react';
import { Chat, Channel, MessageList, Window } from 'stream-chat-react';
import { Users } from 'lucide-react';
import { useEventChat } from '@/feature/event-detail/hooks/useEventChat';
import { useEventChatMembers } from '@/feature/event-detail/hooks/useEventChatMembers';
import { EventChatMemberListModal } from './EventChatMemberListModal';
import 'stream-chat-react/dist/css/v2/index.css';

interface Props {
  chatChannelId: string | undefined;
  eventId: string; // Keep for deep link only
}

export function EventChat({ chatChannelId, eventId }: Props): React.ReactElement {
  const { chatClient, channel, isReady, isLoading, error } = useEventChat(chatChannelId);
  const { memberCount, members } = useEventChatMembers(channel);
  const [showMemberList, setShowMemberList] = useState(false);

  const handleOpenInApp = () => {
    // Try to open the event in the app via deep link
    window.location.href = `wispa://event/${eventId}`;

    // Fallback to download page after a short delay if app isn't installed
    setTimeout(() => {
      window.location.href = '/download';
    }, 1500);
  };

  const handleOpenMemberList = () => {
    setShowMemberList(true);
  };

  const handleCloseMemberList = () => {
    setShowMemberList(false);
  };

  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.headerTitle}>Event Lobby Chat</h3>
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingText}>Loading chat...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.headerTitle}>Event Lobby Chat</h3>
        </div>
        <div style={styles.errorContainer}>
          <div style={styles.errorText}>
            Unable to load chat. Please try again later.
          </div>
        </div>
      </div>
    );
  }

  if (!isReady || !chatClient || !channel) {
    return (
      <div style={styles.container}>
        <div style={styles.header}>
          <h3 style={styles.headerTitle}>Event Lobby Chat</h3>
        </div>
        <div style={styles.loadingContainer}>
          <div style={styles.loadingText}>Connecting...</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Chat client={chatClient}>
        <Channel channel={channel}>
          <Window>
            <div style={styles.header}>
              <h3 style={styles.headerTitle}>Event Lobby Chat</h3>
              <button style={styles.memberButton} onClick={handleOpenMemberList}>
                <Users size={16} color="#999" />
                <span style={styles.memberCount}>{memberCount}</span>
              </button>
            </div>
            <MessageList />
            <div style={styles.ctaContainer}>
              <div style={styles.ctaText}>
                Join the conversation in the app
              </div>
              <button style={styles.ctaButton} onClick={handleOpenInApp}>
                Open in App
              </button>
            </div>
          </Window>
        </Channel>
      </Chat>

      <EventChatMemberListModal
        visible={showMemberList}
        members={members}
        memberCount={memberCount}
        onClose={handleCloseMemberList}
      />
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    backgroundColor: '#fff',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    borderBottom: '1px solid #e0e0e0',
    backgroundColor: '#fff',
    zIndex: 1,
  },
  headerTitle: {
    fontSize: '12px',
    fontWeight: 700,
    color: '#999',
    margin: 0,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  memberButton: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '6px',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '4px',
  },
  memberCount: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#999',
  },
  loadingContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#999',
  },
  loadingText: {
    fontSize: '14px',
  },
  errorContainer: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  errorText: {
    fontSize: '14px',
    color: '#dc3545',
    textAlign: 'center',
  },
  ctaContainer: {
    padding: '16px 20px',
    borderTop: '1px solid #e0e0e0',
    backgroundColor: '#f8f9fa',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
  },
  ctaText: {
    fontSize: '14px',
    color: '#666',
    textAlign: 'center',
  },
  ctaButton: {
    padding: '10px 24px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    backgroundColor: '#007bff',
    border: 'none',
    borderRadius: '24px',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};
